import React from 'react';
import AcquirerIdentity from './AcquirerIdentity';

const moeda = valor => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const inteiro = valor => Number(valor || 0).toLocaleString('pt-BR');
const percentual = (parte, total) => total > 0
  ? `${(parte / total * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
  : '0%';
const competencia = valor => valor ? valor.split('-').reverse().join('/') : '—';

// Cores validadas para daltonismo (sem verde ao lado de vermelho); o cinza
// marca o estado neutro "inativo" e sempre vem com rótulo e número na legenda.
const COR = { ativo: '#2a78d6', alerta: '#eda100', critico: '#d03b3b', neutro: '#8c97a4', homologacao: '#6250d6' };

// Barra de composição: cada segmento proporcional, com dica ao passar o mouse.
function Composicao({ partes, formatar = inteiro, rotulo }) {
  const total = partes.reduce((soma, parte) => soma + Number(parte.valor || 0), 0);
  return <div className="ov-stack" role="img" aria-label={`${rotulo}: ${partes.map(p => `${p.nome} ${formatar(p.valor)}`).join(', ')}`}>
    {total === 0 ? <span className="ov-stack-empty" /> : partes.filter(p => Number(p.valor) > 0).map(parte =>
      <span key={parte.nome} className="ov-seg" style={{ flexGrow: Number(parte.valor), background: parte.cor }}
        data-tip={`${parte.nome}: ${formatar(parte.valor)} (${percentual(Number(parte.valor), total)})`} />)}
  </div>;
}

function Legenda({ partes, formatar = inteiro, total }) {
  return <ul className="ov-legend">
    {partes.map(parte => <li key={parte.nome}>
      <i style={{ background: parte.cor }} aria-hidden="true" />
      <span>{parte.nome}</span>
      <strong>{formatar(parte.valor)}</strong>
      {total !== undefined && <small>{percentual(Number(parte.valor || 0), total)}</small>}
    </li>)}
  </ul>;
}

function Indicador({ titulo, valor, partes, nota }) {
  return <article className="ov-tile">
    <span className="ov-label">{titulo}</span>
    <strong className="ov-value">{inteiro(valor)}</strong>
    {partes ? <>
      <Composicao partes={partes} rotulo={titulo} />
      <Legenda partes={partes} />
    </> : null}
    {nota && <p className="ov-note">{nota}</p>}
  </article>;
}

const Icone = ({ tipo }) => <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  {tipo === 'critico' && <><path d="M12 3 2 21h20L12 3Z" /><path d="M12 10v4M12 17.5h.01" /></>}
  {tipo === 'alerta' && <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>}
  {tipo === 'ok' && <><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></>}
</svg>;

export default function MasterOverview({ dashboard, onAbrirAba }) {
  const fin = dashboard.financeiro || {};
  const projecao = fin.projecao_detalhada;
  const sh = dashboard.software_houses || {};
  const clientes = dashboard.empresas || {};
  const apps = dashboard.aplicativos || {};
  const integradoras = apps.por_integradora || [];
  const totalAtivacoes = integradoras.reduce((total, item) => total + item.quantidade, 0);
  const maxAtivacoes = Math.max(1, ...integradoras.map(item => item.quantidade));

  const vencido = Number(fin.vencido || 0);
  const aVencer = Math.max(0, Number(fin.em_aberto || 0) - vencido);
  const pago = Number(fin.pago_total || 0);
  const cobrancas = [
    { nome: 'Recebido', valor: pago, cor: COR.ativo },
    { nome: 'A vencer', valor: aVencer, cor: COR.alerta },
    { nome: 'Vencido', valor: vencido, cor: COR.critico }
  ];
  const totalCobrancas = pago + aVencer + vencido;

  const geradoMes = Number(fin.asaas_gerado_mes || 0);
  const pagoMes = Number(fin.pago_mes || 0);
  const taxaMes = geradoMes > 0 ? Math.min(100, pagoMes / geradoMes * 100) : 0;
  const mediaPorApp = projecao?.quantidade_aplicativos ? projecao.total / projecao.quantidade_aplicativos : 0;

  const alertas = [
    fin.titulos_vencidos > 0 && { tipo: 'critico', texto: `${inteiro(fin.titulos_vencidos)} título(s) vencido(s) somando ${moeda(vencido)}`, aba: 'inadimplentes' },
    sh.bloqueadas_financeiro > 0 && { tipo: 'critico', texto: `${inteiro(sh.bloqueadas_financeiro)} Software House(s) com bloqueio financeiro`, aba: 'inadimplentes' },
    sh.bloqueadas > 0 && { tipo: 'alerta', texto: `${inteiro(sh.bloqueadas)} Software House(s) bloqueada(s)`, aba: 'software-houses' },
    clientes.bloqueadas > 0 && { tipo: 'alerta', texto: `${inteiro(clientes.bloqueadas)} cliente(s) bloqueado(s)` },
    apps.lancados > 0 && { tipo: 'alerta', texto: `${inteiro(apps.lancados)} aplicativo(s) lançado(s) aguardando ativação` }
  ].filter(Boolean);

  return <div className="ov">
    <section className="ov-finance" aria-label="Indicadores financeiros">
      <article className="ov-card ov-hero">
        <span className="ov-label">Receita prevista · próximo vencimento</span>
        <strong className="ov-hero-value">{moeda(fin.projecao_proximo_mes)}</strong>
        <p className="ov-muted">
          {projecao ? <>Uso de {competencia(projecao.competencia)} · vence em {competencia(projecao.vencimento)}</> : 'Uso do mês atual × plano de cada Software House'}
        </p>
        {projecao && <dl className="ov-facts">
          <div><dt>SHs pagantes</dt><dd>{inteiro(projecao.quantidade_software_houses)}</dd></div>
          <div><dt>Apps utilizados</dt><dd>{inteiro(projecao.quantidade_aplicativos)}</dd></div>
          <div><dt>Média por app</dt><dd>{moeda(mediaPorApp)}</dd></div>
        </dl>}
        <button className="ov-link" onClick={() => onAbrirAba('projecao')}>Ver composição por Software House →</button>
      </article>

      <article className="ov-card">
        <header className="ov-card-head">
          <h3>Cobranças no Asaas</h3>
          <span className="ov-muted">{moeda(fin.asaas_gerado_total)} gerados no total</span>
        </header>
        <Composicao partes={cobrancas} formatar={moeda} rotulo="Situação das cobranças" />
        <Legenda partes={cobrancas} formatar={moeda} total={totalCobrancas} />
        {fin.titulos_vencidos > 0
          ? <button className="ov-status is-critico" onClick={() => onAbrirAba('inadimplentes')}><Icone tipo="critico" />{inteiro(fin.titulos_vencidos)} título(s) em atraso · ver inadimplentes</button>
          : <p className="ov-status is-ok"><Icone tipo="ok" />Nenhum título vencido</p>}
      </article>

      <article className="ov-card">
        <header className="ov-card-head">
          <h3>Neste mês</h3>
          <span className="ov-muted">{new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</span>
        </header>
        <dl className="ov-month">
          <div><dt>Gerado</dt><dd>{moeda(geradoMes)}</dd></div>
          <div><dt>Recebido</dt><dd>{moeda(pagoMes)}</dd></div>
        </dl>
        <div className="ov-meter" role="meter" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(taxaMes)} aria-label="Recebido sobre o gerado no mês">
          <span style={{ width: `${taxaMes}%` }} />
        </div>
        <p className="ov-muted">{geradoMes > 0 ? `${percentual(Math.min(pagoMes, geradoMes), geradoMes)} do valor gerado no mês já foi recebido` : 'Nenhuma cobrança gerada neste mês'}</p>
      </article>
    </section>

    <section className="ov-card" aria-label="Operação da plataforma">
      <header className="ov-card-head"><h3>Operação da plataforma</h3><span className="ov-muted">Clientes e aplicativos de produção; homologação à parte</span></header>
      <div className="ov-ops">
        <Indicador titulo="Software Houses" valor={sh.total} partes={[
          { nome: 'Ativas', valor: sh.ativas, cor: COR.ativo },
          { nome: 'Bloqueadas', valor: sh.bloqueadas, cor: COR.critico },
          { nome: 'Inativas', valor: sh.inativas, cor: COR.neutro }]} />
        <Indicador titulo="Clientes" valor={clientes.total} partes={[
          { nome: 'Ativos', valor: clientes.ativas, cor: COR.ativo },
          { nome: 'Bloqueados', valor: clientes.bloqueadas, cor: COR.critico },
          { nome: 'Inativos', valor: clientes.inativas, cor: COR.neutro }]} />
        <Indicador titulo="Aplicativos" valor={apps.total} partes={[
          { nome: 'Ativos', valor: apps.ativos, cor: COR.ativo },
          { nome: 'Lançados', valor: apps.lancados, cor: COR.alerta },
          { nome: 'Inativos', valor: apps.inativos, cor: COR.neutro },
          { nome: 'Homologação', valor: apps.homologacao, cor: COR.homologacao }]} />
        <Indicador titulo="Pedidos recebidos" valor={dashboard.pedidos?.total} nota="Total enviado pelos aplicativos" />
        <Indicador titulo="Arquivos de carga" valor={dashboard.carga?.arquivos_total}
          nota={`${inteiro(dashboard.carga?.solicitacoes_total)} solicitações de carga pelos apps`} />
      </div>
    </section>

    <section className="ov-bottom">
      <article className="ov-card">
        <header className="ov-card-head">
          <h3>Ativações por adquirente</h3>
          <span className="ov-muted">{inteiro(totalAtivacoes)} apps ativos em produção</span>
        </header>
        {!integradoras.length ? <p className="ov-muted">Nenhum aplicativo ativo.</p> :
          <ul className="ov-bars">
            {integradoras.map(item => <li key={item.integradora} data-tip={`${item.integradora}: ${inteiro(item.quantidade)} app(s) · ${percentual(item.quantidade, totalAtivacoes)}`}>
              <AcquirerIdentity name={item.integradora} />
              <span className="ov-bar-track" aria-hidden="true"><span style={{ width: `${item.quantidade / maxAtivacoes * 100}%` }} /></span>
              <strong>{inteiro(item.quantidade)}</strong>
              <small>{percentual(item.quantidade, totalAtivacoes)}</small>
            </li>)}
          </ul>}
      </article>

      <article className="ov-card">
        <header className="ov-card-head"><h3>Pontos de atenção</h3><span className="ov-muted">{alertas.length ? `${alertas.length} item(ns)` : 'Tudo em dia'}</span></header>
        {!alertas.length ? <p className="ov-status is-ok"><Icone tipo="ok" />Nenhuma pendência no momento</p> :
          <ul className="ov-alerts">
            {alertas.map(alerta => <li key={alerta.texto}>
              {alerta.aba
                ? <button className={`ov-status is-${alerta.tipo}`} onClick={() => onAbrirAba(alerta.aba)}><Icone tipo={alerta.tipo} />{alerta.texto}<span aria-hidden="true">→</span></button>
                : <p className={`ov-status is-${alerta.tipo}`}><Icone tipo={alerta.tipo} />{alerta.texto}</p>}
            </li>)}
          </ul>}
      </article>
    </section>
  </div>;
}
